import Phaser from 'phaser';
import { DEPTH, IS_PORTRAIT_LAYOUT, UI_CENTER_X } from '../core/Config';
import { GameSettings } from '../core/GameSettings';
import { DebugLog } from '../core/DebugLog';
import { createGameKeys, anyJustDown } from '../core/Keybinds';
import { SaveStore } from '../core/SaveStore';
import { ensureImage } from '../core/SceneAssetLoader';
import { NPCCullingManager } from '../core/NPCCulling';
import { PerformanceConfig } from '../core/PerformanceSettings';
import { markSceneInitComplete, markSceneInitStart } from '../core/RuntimeMonitor';
import { transitionTo } from '../core/SceneTransition';
import { NPCBehaviorController } from '../engine/NPCBehaviorController';
import { NPCSprite } from '../engine/NPCSprite';
import { Player } from '../engine/Player';
import { DataRegistry } from '../systems/DataRegistry';
import { DialogueSystem } from '../systems/DialogueSystem';
import { QuestSystem } from '../systems/QuestSystem';
import { CollisionBox, InteractionDef, NPCDef, OccluderDef } from '../types/data';
import { DeveloperOverlay } from '../ui/DeveloperOverlay';
import { InteractionPrompt } from '../ui/InteractionPrompt';
import { UIScene } from './UIScene';

async function loadJson<T>(path: string): Promise<T> {
  DebugLog.info('VillageScene', `load world json ${path}`);
  const response = await fetch(path, { cache: 'no-store' });
  if (!response.ok) throw new Error(`Failed to load ${path}`);
  DebugLog.info('VillageScene', `loaded world json ${path}`);
  return response.json() as Promise<T>;
}

type SceneInitData = { villageId: string; spawnX?: number; spawnY?: number };

export class VillageScene extends Phaser.Scene {
  private dataRegistry!: DataRegistry;
  private player!: Player;
  private keys!: ReturnType<typeof createGameKeys>;
  private dialogueSystem!: DialogueSystem;
  private developerOverlay!: DeveloperOverlay;
  private debugGraphics!: Phaser.GameObjects.Graphics;

  private interactions: InteractionDef[] = [];
  private interactionZones = new Map<string, Phaser.GameObjects.Zone>();
  private worldNPCs = new Map<string, NPCSprite>();
  private npcControllers: NPCBehaviorController[] = [];

  private currentVillageId = 'village01';
  private activeInteractable?: InteractionDef;
  private interactionHint!: Phaser.GameObjects.Text;
  private uiScene?: UIScene;
  private performance!: PerformanceConfig;
  private npcCullManager!: NPCCullingManager;
  private interactionPrompt!: InteractionPrompt;

  private isSceneReady = false;

  // Debug data
  private debugVisible = false;
  private debugLabels: Phaser.GameObjects.Text[] = [];
  private collisionBoxes: CollisionBox[] = [];
  private occluderDefs: OccluderDef[] = [];
  private currentVillageSpawn?: { x: number; y: number };
  private worldStepHandler?: () => void;

  constructor() {
    super('VillageScene');
  }

  private getUiScene(): UIScene | undefined {
    if (this.uiScene) return this.uiScene;
    if (!this.scene.isActive('UIScene')) return undefined;
    this.uiScene = this.scene.get('UIScene') as UIScene;
    return this.uiScene;
  }

  async create(data: SceneInitData) {
    this.isSceneReady = false;
    this.resetRuntimeState();
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.handleShutdown());
    DebugLog.info('VillageScene', 'create start', data);
    markSceneInitStart('VillageScene', data);

    try {
      this.currentVillageId = data.villageId;
      this.dataRegistry = this.registry.get('dataRegistry') as DataRegistry;
      const village = this.dataRegistry.getVillage(data.villageId);

      this.uiScene = this.getUiScene();
      this.performance = this.registry.get('performanceConfig') as PerformanceConfig;

      await ensureImage(this, village.bgKey, village.bgPath);

      const ui = this.getUiScene();
      ui?.events.emit('set-location', village.name);
      ui?.events.emit('set-source', this.dataRegistry.sourceInfo);
      ui?.events.emit('refresh-settings-ui');
      ui?.setActionContext();
      ui?.teleportMenu?.setVillages(this.dataRegistry.getAllVillages(), this.currentVillageId);

      const collisions = await loadJson<CollisionBox[]>(`/data/${village.worldDataDir}/collisions.json`);
      const interactables = await loadJson<InteractionDef[]>(`/data/${village.worldDataDir}/interactables.json`);
      const occluders = await loadJson<OccluderDef[]>(`/data/${village.worldDataDir}/occluders.json`);
      DebugLog.info('VillageScene', 'world data ready', {
        villageId: this.currentVillageId,
        collisions: collisions.length,
        interactables: interactables.length,
        occluders: occluders.length,
      });

      this.collisionBoxes = collisions;
      this.interactions = interactables;
      this.occluderDefs = occluders;
      this.currentVillageSpawn = {
        x: data.spawnX ?? village.playerSpawn.x,
        y: data.spawnY ?? village.playerSpawn.y
      };

      this.physics.world.setBounds(0, 0, village.worldWidth, village.worldHeight);
      this.cameras.main.setBounds(0, 0, village.worldWidth, village.worldHeight);
      this.cameras.main.resetFX();
      this.cameras.main.fadeIn(120, 8, 12, 24);

      this.add
        .image(0, 0, village.bgKey)
        .setOrigin(0)
        .setDisplaySize(village.worldWidth, village.worldHeight)
        .setDepth(DEPTH.GROUND);

      this.keys = createGameKeys(this);
      this.createAnimations();

      this.player = new Player(
        this,
        data.spawnX ?? village.playerSpawn.x,
        data.spawnY ?? village.playerSpawn.y,
        this.keys
      );

      const collisionGroup = this.physics.add.staticGroup();
      collisions.forEach((box) => {
        const rect = this.add.rectangle(box.x, box.y, box.w, box.h, 0xff0000, 0).setOrigin(0);
        this.physics.add.existing(rect, true);
        collisionGroup.add(rect);
      });
      this.physics.add.collider(this.player, collisionGroup);

      this.debugGraphics = this.add.graphics().setDepth(DEPTH.UI + 50).setScrollFactor(1);

      this.interactionHint = this.add
        .text(UI_CENTER_X, 64, '', {
          fontSize: IS_PORTRAIT_LAYOUT ? '32px' : '22px',
          color: '#fff0b5',
          backgroundColor: '#0c120fdd',
          padding: { x: IS_PORTRAIT_LAYOUT ? 18 : 12, y: IS_PORTRAIT_LAYOUT ? 12 : 8 },
          wordWrap: { width: IS_PORTRAIT_LAYOUT ? 760 : 1200 },
        })
        .setPosition(UI_CENTER_X, IS_PORTRAIT_LAYOUT ? 126 : 64)
        .setOrigin(0.5, 0)
        .setScrollFactor(0)
        .setDepth(DEPTH.UI + 2)
        .setVisible(false);

      this.createNPCs();
      this.createInteractionZones(interactables);
      this.createOccluders(occluders);

      this.cameras.main.startFollow(this.player, true, 0.08, 0.08);
      this.cameras.main.setZoom(Number(GameSettings.get('cameraZoom')));

      const latestUi = this.getUiScene();
      if (!latestUi) throw new Error('UIScene must be active before VillageScene creates dialogue UI.');
      this.dialogueSystem = new DialogueSystem(
        this,
        this.keys,
        latestUi.dialogueUI,
        latestUi.choiceUI,
        latestUi.actionPanel
      );

      this.developerOverlay = new DeveloperOverlay(this, {
        worldKey: this.currentVillageId,
        worldType: 'village'
      });

      this.interactionPrompt = new InteractionPrompt(this);
      this.npcCullManager = new NPCCullingManager(this, this.performance.npc);

      this.drawDebug();

      SaveStore.saveSession({
        sceneType: 'village',
        villageId: this.currentVillageId,
        spawnX: this.player.x,
        spawnY: this.player.y
      });

      this.tweens.add({
        targets: this.interactionHint,
        alpha: { from: 0.72, to: 1 },
        duration: 540,
        yoyo: true,
        repeat: -1
      });

      this.isSceneReady = true;
      markSceneInitComplete('VillageScene');
      QuestSystem.record('scene_enter', this.currentVillageId);
      DebugLog.info('VillageScene', 'create complete', { villageId: this.currentVillageId });
    } catch (error) {
      DebugLog.error('VillageScene', 'create failed', error);
      throw error;
    }
  }

  update(time: number) {
    if (!this.isSceneReady) return;
    if (!this.keys || !this.player || !this.dialogueSystem || !this.developerOverlay || !this.npcCullManager) return;

    const ui = this.getUiScene();
    const settingsPanel = ui?.settingsPanel;
    const portraitCatalogPanel = ui?.portraitCatalogPanel;
    const questLogPanel = ui?.questLogPanel;
    const teleportMenu = ui?.teleportMenu;
    const worldEditorPanel = ui?.worldEditorPanel;

    const canOpenMenu = !this.dialogueSystem.isActive();

    if (canOpenMenu && Phaser.Input.Keyboard.JustDown(this.keys.settingsMenu)) {
      ui?.toggleSettingsPanel();
    }

    if (canOpenMenu && Phaser.Input.Keyboard.JustDown(this.keys.portraitCatalog)) {
      ui?.togglePortraitCatalog();
    }

    if (canOpenMenu && Phaser.Input.Keyboard.JustDown(this.keys.teleportMenu)) {
      ui?.toggleTeleportMenu(this.currentVillageId);
    }

    if (canOpenMenu && Phaser.Input.Keyboard.JustDown(this.keys.questLog)) {
      ui?.toggleQuestLogPanel();
    }

    if (canOpenMenu && Phaser.Input.Keyboard.JustDown(this.keys.editorPanel)) {
      ui?.toggleWorldEditorPanel();
    }

    if (anyJustDown([this.keys.cancel], ['cancel'])) {
      ui?.closePanels();
    }

    if (this.developerOverlay.isEnabled()) {
      if (Phaser.Input.Keyboard.JustDown(this.keys.editorGrid)) {
        this.developerOverlay.toggleGrid();
      }
      if (Phaser.Input.Keyboard.JustDown(this.keys.editorSnapDown)) {
        this.developerOverlay.changeSnap(-1);
      }
      if (Phaser.Input.Keyboard.JustDown(this.keys.editorSnapUp)) {
        this.developerOverlay.changeSnap(1);
      }
      worldEditorPanel?.render(this.developerOverlay.getPanelState());
    } else if (worldEditorPanel?.isVisible()) {
      worldEditorPanel.render(this.developerOverlay.getPanelState());
    }

    if (portraitCatalogPanel?.isVisible()) {
      if (anyJustDown(this.keys.choiceUp, ['up'])) portraitCatalogPanel.move(-1);
      if (anyJustDown(this.keys.choiceDown, ['down'])) portraitCatalogPanel.move(1);
      return;
    }

    if (questLogPanel?.isVisible()) {
      if (anyJustDown(this.keys.choiceUp, ['up'])) questLogPanel.move(-1);
      if (anyJustDown(this.keys.choiceDown, ['down'])) questLogPanel.move(1);
      return;
    }

    if (settingsPanel?.isVisible()) {
      if (anyJustDown(this.keys.choiceUp, ['up'])) settingsPanel.move(-1);
      if (anyJustDown(this.keys.choiceDown, ['down'])) settingsPanel.move(1);
      if (anyJustDown(this.keys.confirm, ['confirm', 'interact'])) {
        settingsPanel.confirm();
        this.cameras.main.setZoom(Number(GameSettings.get('cameraZoom')));
        ui?.events.emit('refresh-settings-ui');
      }
      return;
    }

    if (teleportMenu?.isVisible()) {
      if (anyJustDown(this.keys.choiceUp, ['up'])) teleportMenu.move(-1);
      if (anyJustDown(this.keys.choiceDown, ['down'])) teleportMenu.move(1);
      if (anyJustDown(this.keys.confirm, ['confirm', 'interact'])) {
        const selectedVillageId = teleportMenu.getSelectedVillageId();
        if (selectedVillageId) {
          DebugLog.info('VillageScene', 'teleport menu confirm', {
            fromVillageId: this.currentVillageId,
            toVillageId: selectedVillageId,
          });
          teleportMenu.hide();
          SaveStore.saveSession({ sceneType: 'village', villageId: selectedVillageId });
          transitionTo(this, 'VillageScene', { villageId: selectedVillageId });
        }
      }
      return;
    }

    this.player.update(this.dialogueSystem.isActive());
    this.dialogueSystem.update();
    this.developerOverlay.update(this.player);
    this.npcCullManager.update(time, this.npcControllers);

    if (Phaser.Input.Keyboard.JustDown(this.keys.reload)) {
      transitionTo(this, 'VillageScene', {
        villageId: this.currentVillageId,
        spawnX: this.player.x,
        spawnY: this.player.y
      });
    }

    if (Phaser.Input.Keyboard.JustDown(this.keys.debug)) {
      this.debugVisible = !this.debugVisible;
      this.drawDebug();
    }

    if (Phaser.Input.Keyboard.JustDown(this.keys.devOverlay) && this.keys.alt.isDown) {
      this.developerOverlay.toggle();
      worldEditorPanel?.render(this.developerOverlay.getPanelState());
    }

    if (
      !this.dialogueSystem.isActive() &&
      this.activeInteractable &&
      this.activeInteractable.type === 'npc' &&
      anyJustDown(this.keys.interact, ['interact', 'confirm'])
    ) {
      const npc = this.dataRegistry.getNPC(this.activeInteractable.npcId);
      const dialogue = this.dataRegistry.getDialogue(npc.dialogueId);
      this.dialogueSystem.start(dialogue);
    }

    this.interactionHint.setVisible(Boolean(this.activeInteractable) && !this.dialogueSystem.isActive());

    if (!this.dialogueSystem.isActive() && this.activeInteractable?.type === 'npc') {
      const npc = this.dataRegistry.getNPC(this.activeInteractable.npcId);
      this.interactionHint.setText(`${IS_PORTRAIT_LAYOUT ? 'A' : 'F / SPACE / ENTER'}  ·  ${npc.name}와 대화`);
      ui?.setActionContext('대화');
      this.interactionPrompt.showAt(
        this.activeInteractable.zone.x + this.activeInteractable.zone.w / 2,
        this.activeInteractable.zone.y - 16,
        '대화'
      );
    } else if (!this.dialogueSystem.isActive() && this.activeInteractable?.type === 'house_entry') {
      const house = this.dataRegistry.getHouse(this.activeInteractable.houseId);
      this.interactionHint.setText(`${IS_PORTRAIT_LAYOUT ? 'A' : 'F / SPACE / ENTER'}  ·  ${house.name} 들어가기`);
      ui?.setActionContext('입장');
      this.interactionPrompt.showAt(
        this.activeInteractable.zone.x + this.activeInteractable.zone.w / 2,
        this.activeInteractable.zone.y - 16,
        '입장'
      );
    } else {
      ui?.setActionContext();
      this.interactionPrompt.hide();
    }

    const teleportIndex = this.keys.teleports.findIndex((key) => Phaser.Input.Keyboard.JustDown(key));
    if (teleportIndex > 0) {
      const targetId = `village${String(teleportIndex).padStart(2, '0')}`;
      if (this.dataRegistry.villagesById.has(targetId)) {
        DebugLog.info('VillageScene', 'numpad teleport', {
          fromVillageId: this.currentVillageId,
          toVillageId: targetId,
          numpad: teleportIndex,
        });
        SaveStore.saveSession({ sceneType: 'village', villageId: targetId });
        transitionTo(this, 'VillageScene', { villageId: targetId });
      }
    }

    SaveStore.saveSession({
      sceneType: 'village',
      villageId: this.currentVillageId,
      spawnX: this.player.x,
      spawnY: this.player.y
    });
  }

  private createAnimations() {
    if (this.anims.exists('player-down-walk')) return;

    this.anims.create({
      key: 'player-down-walk',
      frames: this.anims.generateFrameNumbers('player_sheet', { start: 0, end: 3 }),
      frameRate: 8,
      repeat: -1
    });

    this.anims.create({
      key: 'player-up-walk',
      frames: this.anims.generateFrameNumbers('player_sheet', { start: 4, end: 7 }),
      frameRate: 8,
      repeat: -1
    });

    this.anims.create({
      key: 'player-left-walk',
      frames: this.anims.generateFrameNumbers('player_sheet', { start: 8, end: 11 }),
      frameRate: 8,
      repeat: -1
    });

    this.anims.create({
      key: 'player-right-walk',
      frames: this.anims.generateFrameNumbers('player_sheet', { start: 12, end: 15 }),
      frameRate: 8,
      repeat: -1
    });

    this.anims.create({
      key: 'player-down-idle',
      frames: [{ key: 'player_sheet', frame: 0 }],
      frameRate: 1
    });

    this.anims.create({
      key: 'player-up-idle',
      frames: [{ key: 'player_sheet', frame: 4 }],
      frameRate: 1
    });

    this.anims.create({
      key: 'player-left-idle',
      frames: [{ key: 'player_sheet', frame: 8 }],
      frameRate: 1
    });

    this.anims.create({
      key: 'player-right-idle',
      frames: [{ key: 'player_sheet', frame: 12 }],
      frameRate: 1
    });
  }

  private createNPCs() {
    const npcs = [...this.dataRegistry.npcsById.values()].filter(
      (npc) => npc.villageId === this.currentVillageId
    );

    npcs.forEach((npc: NPCDef) => {
      const sprite = new NPCSprite(this, npc.x, npc.y, npc.spriteKey);
      this.worldNPCs.set(npc.id, sprite);
      this.npcControllers.push(new NPCBehaviorController(this, sprite, npc.move));
    });
  }

  private createInteractionZones(interactions: InteractionDef[]) {
    interactions.forEach((interaction) => {
      const zone = this.add
        .zone(interaction.zone.x, interaction.zone.y, interaction.zone.w, interaction.zone.h)
        .setOrigin(0);

      this.physics.add.existing(zone, true);
      this.interactionZones.set(interaction.id, zone);

      this.physics.add.overlap(this.player, zone, () => {
        this.activeInteractable = interaction;
      });
    });

    this.worldStepHandler = () => {
      let found: InteractionDef | undefined;

      this.interactions.forEach((interaction) => {
        const zone = this.interactionZones.get(interaction.id);
        if (!zone) return;

        if (Phaser.Geom.Intersects.RectangleToRectangle(this.player.getBounds(), zone.getBounds())) {
          found = interaction;
        }
      });

      this.activeInteractable = found;

      if (!found) {
        this.interactionHint.setVisible(false);
        this.interactionPrompt.hide();
      }
    };
    this.physics.world.on('worldstep', this.worldStepHandler);
  }

  private resetRuntimeState() {
    this.npcControllers = [];
    this.worldNPCs.clear();
    this.interactionZones.clear();
    this.interactions = [];
    this.activeInteractable = undefined;
    this.worldStepHandler = undefined;
  }

  private handleShutdown() {
    this.isSceneReady = false;
    this.worldStepHandler = undefined;
    this.npcControllers = [];
    this.worldNPCs.clear();
    this.interactionZones.clear();
    this.activeInteractable = undefined;
    SaveStore.flushSession();
  }

  private createOccluders(occluders: OccluderDef[]) {
    occluders.forEach((item) => {
      this.add
        .image(item.x, item.y, item.spriteKey)
        .setOrigin(0.5, 1)
        .setDepth(DEPTH.OBJECT_BASE + item.y);

      if (item.frontSpriteKey) {
        this.add
          .image(item.x, item.y, item.frontSpriteKey)
          .setOrigin(0.5, 1)
          .setDepth(DEPTH.OCCLUDER_FRONT + item.y);
      }
    });
  }

  private clearDebugLabels() {
    this.debugLabels.forEach((label) => label.destroy());
    this.debugLabels = [];
  }

  private addDebugLabel(x: number, y: number, text: string, color = '#ffffff') {
    const label = this.add.text(x, y, text, {
      fontSize: '12px',
      color,
      backgroundColor: '#000000aa',
      padding: { x: 4, y: 2 }
    }).setDepth(DEPTH.UI + 60);

    this.debugLabels.push(label);
  }

  private drawDebug() {
    this.debugGraphics.clear();
    this.clearDebugLabels();

    if (!this.debugVisible) return;

    // 1) 충돌 박스 - 빨강
    this.debugGraphics.lineStyle(2, 0xef4444, 0.95);
    this.collisionBoxes.forEach((box, index) => {
      this.debugGraphics.strokeRect(box.x, box.y, box.w, box.h);
      this.addDebugLabel(box.x, box.y - 16, `collision_${index}`, '#ff6666');
    });

    // 2) 상호작용 존 - 타입별 색상
    this.interactions.forEach((interaction) => {
      let color = 0xf59e0b;
      let labelColor = '#fbbf24';

      if (interaction.type === 'house_entry') {
        color = 0x3b82f6;
        labelColor = '#60a5fa';
      }
      if (interaction.type === 'npc') {
        color = 0xa855f7;
        labelColor = '#d8b4fe';
      }

      this.debugGraphics.lineStyle(2, color, 0.95);
      this.debugGraphics.strokeRect(
        interaction.zone.x,
        interaction.zone.y,
        interaction.zone.w,
        interaction.zone.h
      );

      this.addDebugLabel(
        interaction.zone.x,
        interaction.zone.y - 16,
        `${interaction.type}:${interaction.id}`,
        labelColor
      );
    });

    // 3) 플레이어 스폰 위치 - 초록 십자
    if (this.currentVillageSpawn) {
      const x = this.currentVillageSpawn.x;
      const y = this.currentVillageSpawn.y;

      this.debugGraphics.lineStyle(3, 0x22c55e, 1);
      this.debugGraphics.strokeCircle(x, y, 12);
      this.debugGraphics.lineBetween(x - 14, y, x + 14, y);
      this.debugGraphics.lineBetween(x, y - 14, x, y + 14);

      this.addDebugLabel(x + 16, y - 10, 'spawn', '#4ade80');
    }

    // 4) 오클루더 기준점 - 하늘색
    this.occluderDefs.forEach((item, index) => {
      this.debugGraphics.lineStyle(2, 0x06b6d4, 0.95);
      this.debugGraphics.strokeCircle(item.x, item.y, 10);
      this.addDebugLabel(item.x + 12, item.y - 10, `occluder_${index}`, '#67e8f9');
    });
  }
}
